import { styled } from 'linaria/react';
import { COLORS, FONTS, BREAKPOINTS } from 'styles/constants';

export const StyledModalOverlay = styled.div<{ isOpen: boolean }>`
  align-items: flex-end;
  background-color: ${COLORS.black[50]};
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  position: fixed;
  right: 0;
  top: 0;
  transition: opacity 0.2s ease-in-out;
  z-index: 10000000;
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};

  @media screen and (min-width: ${BREAKPOINTS.s}px) {
    align-items: center;
    backdrop-filter: blur(12px);
  }
`;

export const StyledModalContainer = styled.div<{ isOpen: boolean }>`
  background: ${COLORS.brown.dark[100]};
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 46px);
  max-height: calc(100vh - 46px);
  max-width: 518px;
  overflow: hidden;
  padding-bottom: env(safe-area-inset-bottom, 0);
  position: relative;
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;

  @media screen and (min-width: ${BREAKPOINTS.s}px) {
    border-radius: 20px;
    height: auto;
    max-height: 80vh;
    padding-bottom: 0;
  }
`;

export const StyledModalHeader = styled.header`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0;
`;

export const StyledViewControls = styled.div`
  align-items: center;
  display: flex;
  gap: 2px;
  height: 44px;
  padding: 0 24px;
`;

export const StyledCancelButton = styled.button`
  align-items: center;
  backdrop-filter: blur(2px);
  background: transparent;
  border: none;
  border-radius: 22px;
  color: ${COLORS.white[100]};
  cursor: pointer;
  display: flex;
  font: 600 14px/1.4 ${FONTS.secondary};
  justify-content: center;
  padding: 12px 0;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`;

export const StyledHeaderTitle = styled.div`
  padding: 32px 24px 16px;
`;

export const StyledTitle = styled.h2`
  color: ${COLORS.white[100]};
  font: 400 22px/1.3 ${FONTS.primary};
  margin: 0;
`;

export const StyledModalContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 12px 24px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StyledCardsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px 16px;
  justify-content: flex-start;
  padding: 0;
`;

export const StyledSparkCard = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: calc(50% - 8px);
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
    transform: scale(0.98);
  }

  &:active {
    transform: scale(0.96);
  }
`;

export const StyledCardCover = styled.div<{ bgImage?: string }>`
  aspect-ratio: 175 / 222;
  background-color: ${COLORS.brown.dark[100]};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

export const StyledCardOverlay = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding: 12px;
  position: absolute;
  width: 100%;
`;

export const StyledCardCategory = styled.span`
  color: ${COLORS.white[80]};
  font: 500 11px/1.4 ${FONTS.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const StyledCardTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

export const StyledCardTitle = styled.h3`
  color: ${COLORS.white[100]};
  font: 700 18px/1.2 ${FONTS.primary};
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

export const StyledCardSubtitle = styled.span`
  color: ${COLORS.white[80]};
  font: 400 12px/1.3 ${FONTS.secondary};
`;

export const StyledCardBottom = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledFacepile = styled.div`
  display: flex;
  padding-right: 8px;
`;

export const StyledFacepileAvatar = styled.div<{ src?: string }>`
  background-color: ${COLORS.brown.light[100]};
  background-image: ${({ src }) => (src ? `url(${src})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 60px;
  height: 20px;
  margin-right: -8px;
  overflow: hidden;
  width: 20px;

  &:last-child {
    margin-right: 0;
  }
`;

export const StyledLoadingContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 200px;
`;

// Style Selection View Styles
export const StyledStyleViewControls = styled.div`
  align-items: center;
  display: flex;
  height: 44px;
  justify-content: space-between;
  margin-top: 20px;
  padding: 0 24px;
`;

export const StyledCreateButton = styled.button<{ disabled?: boolean }>`
  align-items: center;
  background: ${({ disabled }) => (disabled ? COLORS.white[50] : COLORS.white[100])};
  border: none;
  border-radius: 22px;
  color: ${COLORS.brown.dark[100]};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  font: 600 14px/1.4 ${FONTS.secondary};
  justify-content: center;
  padding: 12px 20px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 1 : 0.9)};
  }
`;

export const StyledPreviewContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 12px 24px;
`;

export const StyledPreviewCard = styled.div<{ bgImage?: string }>`
  aspect-ratio: 324 / 408;
  background-color: ${COLORS.brown.dark[100]};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 8px;
  max-height: 408px;
  max-width: 324px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

export const StyledStyleSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 16px;
  padding: 24px 0;
`;

export const StyledStyleHeader = styled.div`
  padding: 0 24px;
`;

export const StyledStyleLabel = styled.span`
  color: ${COLORS.white[100]};
  font: 400 14px/1.4 ${FONTS.secondary};
`;

export const StyledStyleCarousel = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 6px 12px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StyledStyleTile = styled.div<{ bgImage?: string; isSelected?: boolean }>`
  background-color: ${COLORS.brown.dark[100]};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  height: 88px;
  overflow: hidden;
  position: relative;
  transition: transform 0.15s ease;
  width: 70px;

  &::after {
    border: 1.5px solid ${COLORS.white[100]};
    border-radius: 14px;
    bottom: -4px;
    content: '';
    left: -4px;
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0)};
    pointer-events: none;
    position: absolute;
    right: -4px;
    top: -4px;
    transition: opacity 0.15s ease;
    z-index: 1;
  }

  &:hover {
    transform: scale(0.98);
  }

  &:active {
    transform: scale(0.96);
  }
`;

export const StyledStyleTileVideo = styled.video`
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

export const StyledPreviewVideo = styled.video`
  height: 100%;
  object-fit: cover;
  position: absolute;
  width: 100%;
`;

export const StyledThoughtInputSection = styled.div`
  background: ${COLORS.brown.dark[100]};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

export const StyledThoughtInputBorder = styled.div`
  background: ${COLORS.white[30]};
  height: 1px;
  margin: 0 24px;
`;

export const StyledThoughtInputRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 16px 24px;
`;

export const StyledThoughtAvatar = styled.div<{ src?: string }>`
  background-color: ${COLORS.brown.light[100]};
  background-image: ${({ src }) => (src ? `url(${src})` : 'none')};
  background-position: center;
  background-size: cover;
  border-radius: 180px;
  flex-shrink: 0;
  height: 44px;
  overflow: hidden;
  width: 44px;
`;

export const StyledThoughtInput = styled.input`
  background: transparent;
  border: none;
  color: ${COLORS.white[100]};
  flex: 1;
  font: 400 14px/1.4 ${FONTS.secondary};
  outline: none;

  &::placeholder {
    color: ${COLORS.brown.light[100]};
  }
`;

export const StyledSlideContainer = styled.div<{ step: number; panelCount?: number }>`
  display: flex;
  flex: 1;
  overflow: hidden;
  width: ${({ panelCount }) => (panelCount ?? 2) * 100}%;
  transform: translateX(${({ step, panelCount }) => step * (-100 / (panelCount ?? 2))}%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const StyledSlidePanel = styled.div<{ panelCount?: number }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  width: ${({ panelCount }) => 100 / (panelCount ?? 2)}%;
`;

// Intro Step Styles
export const StyledIntroContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 12px 24px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const StyledIntroCard = styled.div`
  background: linear-gradient(180deg, #2a657e 0%, #b977ad 100%);
  border-radius: 16px;
  margin: 0 auto;
  max-width: 350px;
  overflow: hidden;
  padding-bottom: 20px;
  position: relative;
  width: 100%;
`;

export const StyledIntroCardNoise = styled.div`
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  inset: 0;
  opacity: 0.08;
  pointer-events: none;
  position: absolute;
`;

export const StyledIntroCardTitle = styled.h2`
  color: ${COLORS.white[100]};
  font: 400 32px/1.1 ${FONTS.primary};
  margin: 0;
  padding: 24px 24px 0;
  text-align: center;
  width: 100%;
`;

export const StyledIntroCardImage = styled.img`
  border-radius: 8px;
  display: block;
  margin: 16px auto 0;
  max-width: 218px;
  object-fit: contain;
  width: 80%;
`;

export const StyledIntroTextSection = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 24px 0;
  text-align: center;
`;

export const StyledIntroHeading = styled.h3`
  color: ${COLORS.white[100]};
  font: 400 22px/1.3 ${FONTS.primary};
  margin: 0;
`;

export const StyledIntroDescription = styled.p`
  color: ${COLORS.white[60]};
  font: 600 14px/1.4 ${FONTS.secondary};
  margin: 0;
`;

export const StyledIntroNextButton = styled.button`
  align-items: center;
  background: ${COLORS.white[100]};
  border: none;
  border-radius: 22px;
  color: ${COLORS.brown.dark[100]};
  cursor: pointer;
  display: flex;
  font: 600 14px/1.4 ${FONTS.secondary};
  justify-content: center;
  margin: auto 24px 24px;
  padding: 12px 20px;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
`;
